import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaLinkedin, FaGithub, FaInstagram } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const contactInfo = [
  {
    title: 'Email',
    value: 'info@nayrix.com',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: 'Phone',
    value: '+91 8848377626',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
  },
  {
    title: 'Location',
    value: 'Kerala, India',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const socials = [
  { label: 'LinkedIn', url: 'https://www.linkedin.com/company/nayrix/', icon: <FaLinkedin className="w-5 h-5" /> },
  { label: 'X', url: 'https://x.com/nayrix_guild', icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="currentColor">
      <g>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
      </g>
    </svg>
  ) },
  { label: 'GitHub', url: 'https://github.com/nayrix-hub', icon: <FaGithub className="w-5 h-5" /> },
  { label: 'Instagram', url: 'https://www.instagram.com/nayrix_infotech/', icon: <FaInstagram className="w-5 h-5" /> },
];

const SERVICE_ID = 'service_pwzisde';  // TODO: Replace with your EmailJS Service ID
const TEMPLATE_ID = 'template_fy0qqk2'; // TODO: Replace with your EmailJS Template ID
const USER_ID = 'CdPkENrVO0vYTZAw3'; // TODO: Replace with your EmailJS Public Key

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef();

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.company || !formData.message) {
      setError('Please fill in all fields.');
      return;
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company,
          message: formData.message,
          time: new Date().toLocaleString(),
          'g-recaptcha-response': recaptchaToken,
        },
        USER_ID
      );
      setSubmitted(true);
      setFormData({ name: '', email: '', company: '', message: '' });
      setRecaptchaToken('');
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } catch (err) {
      setError('Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
      setTimeout(() => setSubmitted(false), 4000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <Helmet>
        <title>Contact Nayrix | Get in Touch</title>
        <meta name="description" content="Contact Nayrix for enterprise software, SaaS, API, and cloud solutions. Let's discuss your project!" />
        <meta property="og:title" content="Contact Nayrix | Get in Touch" />
        <meta property="og:description" content="Contact Nayrix for enterprise software, SaaS, API, and cloud solutions. Let's discuss your project!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nayrix.com/contact" />
        <link rel="canonical" href="https://nayrix.com/contact" />
      </Helmet>
    <main className="relative">
      {/* Hero/Intro Section */}
      <section className="relative py-24 bg-gradient-to-br from-primary-50 via-light-bg to-primary-100 dark:from-dark-bg dark:via-dark-bg/80 dark:to-dark-bg/60 animate-gradient-move">
        <div className="container px-4 mx-auto text-center max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6 dark:text-dark-text text-light-text"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl dark:text-dark-text-secondary text-light-text-secondary mb-8"
          >
            Let's discuss how our enterprise solutions can transform your business.
          </motion.p>
        </div>
      </section>
      {/* Contact Info & Form */}
      <section className="py-20 bg-light-bg dark:bg-dark-bg/50">
        <div className="container px-4 mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="p-8 rounded-2xl bg-white/80 dark:bg-dark-bg/70 shadow-lg border border-neutral-200 dark:border-dark-border backdrop-blur-md flex flex-col gap-8">
                <h3 className="text-2xl font-semibold mb-2 dark:text-dark-text text-light-text">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  {contactInfo.map((info) => (
                    <div key={info.title} className="flex items-start space-x-4">
                      <div className="p-3 rounded-full dark:bg-dark-accent/10 bg-light-accent/10 dark:text-dark-accent text-light-accent">
                        {info.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium mb-1 dark:text-dark-text text-light-text">{info.title}</h4>
                        <p className="dark:text-dark-text-secondary text-light-text-secondary">{info.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-white/80 dark:bg-dark-bg/70 shadow-lg border border-neutral-200 dark:border-dark-border backdrop-blur-md flex flex-col gap-6">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2 dark:text-dark-text text-light-text">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 dark:bg-dark-bg/50 bg-light-bg/50 dark:border-dark-border border-light-border border rounded-lg focus:outline-none focus:ring-2 focus:ring-dark-accent dark:focus:ring-dark-accent focus:border-transparent dark:text-dark-text text-light-text"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2 dark:text-dark-text text-light-text">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 dark:bg-dark-bg/50 bg-light-bg/50 dark:border-dark-border border-light-border border rounded-lg focus:outline-none focus:ring-2 focus:ring-dark-accent dark:focus:ring-dark-accent focus:border-transparent dark:text-dark-text text-light-text"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2 dark:text-dark-text text-light-text">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 dark:bg-dark-bg/50 bg-light-bg/50 dark:border-dark-border border-light-border border rounded-lg focus:outline-none focus:ring-2 focus:ring-dark-accent dark:focus:ring-dark-accent focus:border-transparent dark:text-dark-text text-light-text"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2 dark:text-dark-text text-light-text">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 dark:bg-dark-bg/50 bg-light-bg/50 dark:border-dark-border border-light-border border rounded-lg focus:outline-none focus:ring-2 focus:ring-dark-accent dark:focus:ring-dark-accent focus:border-transparent dark:text-dark-text text-light-text"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-center my-2">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LfTGnUrAAAAAIGs5YeZl86fgQMQaJzfIa--pgj-"
                    onChange={handleRecaptchaChange}
                    theme="light"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full px-8 py-3 rounded-full dark:bg-dark-accent hover:bg-dark-accent-hover bg-light-accent hover:bg-light-accent-hover text-white font-display font-semibold text-base transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
                  disabled={loading || !recaptchaToken}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="text-red-500 text-sm mt-2 text-center"
                    >
                      {error}
                    </motion.div>
                  )}
                  {submitted && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="text-green-600 text-sm mt-2 text-center"
                    >
                      Thank you! Your message has been sent.
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </motion.div>
          </div>
          {/* Social/Contact Buttons */}
          <div className="mt-16 flex flex-wrap justify-center gap-4">
            {socials.map(social => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/60 dark:bg-dark-bg/60 shadow border dark:border-dark-border border-light-border text-primary-600 dark:text-primary-400 font-medium hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-dark-bg transition-all duration-300"
              >
                {social.icon}
                {social.label}
              </a>
            ))}
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default Contact; 